import React, { FC, useCallback, useMemo, useState } from 'react';
import { invalidateGenreProducts, useGenreProducts } from './api/products/useGenreProducts';
import { useCollectionGenres } from './api/genres/useCollectionGenres';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { ContainerInnerWithColumns } from '../../../shared/common/ContainerInner';
import { FilterAndProductListWrapper } from '../../../shared/common/FilterAndProductListWrapper';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { SidebarContainer } from '../../../shared/common/SidebarContainer';
import { GenreSidebar } from './GenreSidebar';
import styled from 'styled-components';
import ProductList from '../../../shared/common/ProductList';
import { useParams } from 'react-router';
import { FilterBar } from '../../../shared/common/FilterBar';
import { CollectionByGenreHeader } from './CollectionByGenreHeader';
import Pagination from '../../../shared/common/Pagination';
import CollectionListItem from '../../../shared/common/ListItems/CollectionListItem';
import NoElementsFound, { NO_ELEMENTS_PRODUCT } from '../../../shared/components/NoElementsFound';
import { AgeCategory } from '../../../../models/AgeCategory';
import { getLoanFormat } from '../../../shared/utils/getLoanFormat';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchByGenreAggregationsQuickSearch } from '../../../api/collection/your-collection/by-genre/fetchByGenreAggregationsQuicksearch';
import { AuthenticationState } from '../../../store/authentication/state';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchByGenreAggregations } from '../../../api/collection/your-collection/by-genre/fetchByGenreAggregations';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { useAddProductsToGroupFromCollection } from '../../../api/library-managed/useAddProductsToGroupFromCollection';
import { CollectionProductTile } from '../CollectionProductTile';
import { useAddBulkFromCollectionToCart } from '../../../api/shopping-cart/useAddFromCollectionToCart';
import { SOURCE_COLLECTION_BY_GENRE } from '../../../store/constants';
import { SelectionList } from '../../../../models/SelectionList';
import { useAddBulkFromCollectionToList } from '../../../api/selection-list/useAddFromCollectionToList';
import { Article, SubscriptionPeriodType } from '../../../../models/Article';
import { CopyDetailsPopup } from '../copyDetailsPopup/CopyDetailsPopup';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { PriceTier } from '../../../../models/PriceTier';
import { useProductSelection } from '../../../shared/product-selection/useProductSelection';
import {
    getProductSelectionCount,
    hasProductSelection,
    nothingSelected
} from '../../../shared/product-selection/ProductSelectionUtils';
import { UnavailableProductsModal } from '../../../shared/common/UnavailableProductsModal';
import { useDisplayTypeSearchParam } from '../../../shared/utils/useDisplayTypeSearchParam';
import { BreadcrumbsState } from '../../../shared/breadcrumbs/types';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../shared/loanFormat/constants';
import { useAuthentication } from '../../../app-store/authentication/useAuthentication';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../../shared/filter/useProductFilters';

const FullWidthContainer = styled.div<{ isLoading: boolean }>`
    width: 100%;
    min-height: ${props => (props.isLoading ? '100vh' : '')};
`;

const FilterBarWithSpace = styled(FilterBar)`
    padding: 0 0 25px 0;
`;

type GenreParams = { genre?: string; subgenre?: string };

const getSelectedGenre = ({ genre, subgenre }: GenreParams) =>
    genre ? decodeURIComponent(genre) + (subgenre ? `->${decodeURIComponent(subgenre)}` : '') : undefined;

export const CollectionByGenrePage: FC = () => {
    const [selectedProduct, setSelectedProduct] = useState<(ProductMetaData & Article) | undefined>(undefined);

    const authentication = useAuthentication();
    const { toggleSelectionMode, productSelection, cancelSelection, selectAll, onSelectButtonClick } =
        useProductSelection();
    const { genre, subgenre, loanFormat: loanFormatPathParam } = useParams<'genre' | 'subgenre' | 'loanFormat'>();
    const loanFormat = getLoanFormat(loanFormatPathParam);

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultPageSize: 54,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const { productFilter } = useProductFilters();
    const { value: ageCategory } = useAgeCategorySearchParam();
    const { display } = useDisplayTypeSearchParam();

    const {
        data: collectionGenres,
        isLoading: isCollectionGenresLoading,
        isSuccess: isCollectionGenresSuccess,
        defaultGenre,
        hasGenres
    } = useCollectionGenres({
        ageCategory,
        loanFormat
    });

    const selectedGenre = getSelectedGenre({ genre, subgenre }) ?? defaultGenre;

    const { data: genreProducts, isLoading: isProductsLoading } = useGenreProducts({
        ageCategory,
        loanFormat: loanFormat.requestKey,
        genre: selectedGenre,
        filterMap: productFilter,
        pagination: { sortBy, page, pageSize }
    });

    const products = genreProducts?.hits ?? [];
    const totalProducts = genreProducts?.totalHits ?? 0;

    const breadcrumbs: BreadcrumbsState = {
        key: 'by-genre',
        label: 'By Genre',
        buckets: collectionGenres?.buckets || [],
        activeBucket: selectedGenre
    };

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => byGenrePageFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'byGenrePage',
            loanFormat.requestKey!,
            'aggregation',
            { ageCategory, genre: selectedGenre, subgenre },
            filters,
            expandedFilterGroups
        ],
        [ageCategory, loanFormat.requestKey, selectedGenre, subgenre]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchByGenreAggregations({
                ...options,
                loanFormat: loanFormat.requestKey!,
                ageCategory,
                genre: selectedGenre,
                subgenre
            }),
        [ageCategory, loanFormat.requestKey, selectedGenre, subgenre]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchByGenreAggregationsQuickSearch({
                ...options,
                ageCategory,
                genre: selectedGenre,
                subgenre,
                loanFormat: loanFormat.requestKey!
            }),
        [ageCategory, loanFormat.requestKey, selectedGenre, subgenre]
    );
    //endregion

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateGenreProducts().then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const { isLoading: isLoadingAddProductsToCart, addToCartBulk } = useAddBulkFromCollectionToCart({
        loanFormat: loanFormat,
        ageCategory,
        source: SOURCE_COLLECTION_BY_GENRE,
        productFilter,
        genreId: selectedGenre,
        onSuccess: () => {
            invalidateGenreProducts().then();
            cancelSelection();
        },
        productSelection
    });

    const { isLoading: isLoadingAddProductsToList, addToListBulk } = useAddBulkFromCollectionToList({
        loanFormat: loanFormat,
        ageCategory,
        source: SOURCE_COLLECTION_BY_GENRE,
        genreId: selectedGenre,
        productSelection,
        onSuccess: () => {
            cancelSelection();
        },
        productFilter
    });

    const { addToGroupBulk } = useAddProductsToGroupFromCollection({
        source: SOURCE_COLLECTION_BY_GENRE,
        ageCategory,
        productSelection,
        genre: selectedGenre,
        loanFormat,
        onSuccess: () => {
            cancelSelection();
        },
        productFilter
    });

    const onBulkAddToList = async (
        selectionList: SelectionList,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => {
        return addToListBulk(selectionList.id, performChecks, priceTier, subscriptionPeriodType);
    };

    if (isCollectionGenresSuccess && !hasGenres) {
        return <NoElementsFound type={NO_ELEMENTS_PRODUCT} />;
    }

    const breadcrumbNavLinkParams = `?${new URLSearchParams({
        ageCategory,
        display,
        ...(displayFilter ? { displayFilter: '' } : {})
    }).toString()}`;

    const loading = isCollectionGenresLoading || isProductsLoading;

    return (
        <ContainerInnerWithColumns>
            <LoadingWrapper>
                <LoadingOverlay loading={loading} />
            </LoadingWrapper>

            {selectedProduct ? (
                <CopyDetailsPopup
                    onClosePopup={() => setSelectedProduct(undefined)}
                    onAddPrice={changePrice}
                    product={selectedProduct}
                    shopLanguage={undefined}
                />
            ) : null}

            <CollectionByGenreHeader
                loanFormat={loanFormat}
                productSelectionCount={getProductSelectionCount(productSelection)}
                isInSelectionMode={hasProductSelection(productSelection)}
                cancelSelection={cancelSelection}
                onBulkAddToCart={addToCartBulk}
                onBulkAddToGroup={addToGroupBulk}
                onBulkAddToList={onBulkAddToList}
                breadcrumbs={breadcrumbs}
                topGenre={
                    collectionGenres?.topBuckets && collectionGenres.topBuckets.length > 0
                        ? collectionGenres?.topBuckets[0].key
                        : undefined
                }
                queryParamString={breadcrumbNavLinkParams}
                containsEpressProducts={loanFormat.requestKey === LOANFORMAT_EPRESS_OBJECT.requestKey}
                isNothingSelected={nothingSelected(productSelection)}
            />

            <FilterBarWithSpace
                sortBy={sortBy}
                totalProducts={totalProducts}
                productSelection={productSelection}
                pageSize={pageSize}
                hideGroupBy={true}
                displayType={display}
                selectAll={() => selectAll(totalProducts)}
                toggleSelectionMode={toggleSelectionMode}
            />
            <FilterAndProductListWrapper>
                {displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={visibleFilterGroups}
                        buildQueryKey={aggregationsQueryKeyBuilder}
                        fetchAggregations={fetchAggregations}
                        fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                    />
                ) : (
                    <SidebarContainer className="sidebar">
                        {collectionGenres && selectedGenre && loanFormat ? (
                            <GenreSidebar
                                genres={collectionGenres}
                                selectedGenre={selectedGenre}
                                loanFormat={loanFormat}
                            />
                        ) : null}
                    </SidebarContainer>
                )}
                <FullWidthContainer
                    isLoading={
                        isProductsLoading ||
                        isCollectionGenresLoading ||
                        isLoadingAddProductsToCart ||
                        isLoadingAddProductsToList
                    }
                >
                    {!loading ? (
                        totalProducts > 0 ? (
                            <>
                                <ProductList
                                    products={products}
                                    displayType={display}
                                    productSelection={productSelection}
                                    onSelectProduct={onSelectButtonClick}
                                    isProductSelectable={true}
                                    onAddPrice={changePrice}
                                >
                                    {({ key, isListView, ...value }) =>
                                        isListView ? (
                                            <CollectionListItem
                                                {...value}
                                                key={key}
                                                showManagePopup={() => setSelectedProduct(value.product)}
                                            />
                                        ) : (
                                            <CollectionProductTile
                                                {...value}
                                                key={key}
                                                showManagePopup={() => setSelectedProduct(value.product)}
                                                isProductGroupTile={false}
                                            />
                                        )
                                    }
                                </ProductList>
                                <div style={{ margin: '40px 0' }} className="bottom-pagination">
                                    <Pagination totalItems={totalProducts} pageSize={pageSize} />
                                </div>
                            </>
                        ) : (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCT} />
                        )
                    ) : null}
                </FullWidthContainer>
            </FilterAndProductListWrapper>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </ContainerInnerWithColumns>
    );
};

const byGenrePageFilterGroups = (authentication: AuthenticationState, ageCategory?: AgeCategory) => [
    ...defaultVisibleFilterGroups(authentication, ageCategory),
    'stockUsageCondition'
];
